/* TreeBros v8: height pills on cards + independent View All / Show Less per section (all products, smooth). */
(() => {
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------- cards: height chips ---------------- */
  function bindCard(card) {
    if (card.dataset.tbBound) return;
    card.dataset.tbBound = '1';
    const links = card.querySelectorAll('[data-tb-card-link]');
    const img = card.querySelector('[data-tb-card-img]') || card.querySelector('.tb-card__img');
    const base = card.dataset.productUrl;
    card.querySelectorAll('.tb-size').forEach((chip) => {
      chip.addEventListener('click', () => {
        card.querySelectorAll('.tb-size').forEach((c) => {
          const on = c === chip;
          c.classList.toggle('is-active', on);
          c.setAttribute('aria-checked', on ? 'true' : 'false');
          c.tabIndex = on ? 0 : -1;
        });
        const url = `${base}?variant=${chip.dataset.variantId}`;
        links.forEach((a) => a.setAttribute('href', url));
        const price = card.querySelector('[data-tb-card-price]');
        if (price && chip.dataset.price) {
          price.textContent = chip.dataset.price;
          price.classList.toggle('tb-card__price--soon', chip.dataset.priceSoon === 'true');
        }
        if (img && chip.dataset.imgSrc && img.getAttribute('src') !== chip.dataset.imgSrc) {
          img.setAttribute('srcset', chip.dataset.imgSrcset || '');
          img.setAttribute('src', chip.dataset.imgSrc);
        }
      });
    });
    // arrow-key navigation inside the radiogroup
    const group = card.querySelector('.tb-sizes');
    if (group) group.addEventListener('keydown', (e) => {
      if (!['ArrowRight', 'ArrowLeft'].includes(e.key)) return;
      const chips = [...group.querySelectorAll('.tb-size')];
      const i = chips.indexOf(document.activeElement);
      if (i < 0) return;
      const next = chips[(i + (e.key === 'ArrowRight' ? 1 : chips.length - 1)) % chips.length];
      next.focus(); next.click(); e.preventDefault();
    });
  }

  /* ---------------- expandable section ---------------- */
  function columns(grid) {
    return getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length || 1;
  }
  function firstRowHeight(grid) {
    const cards = [...grid.children];
    if (!cards.length) return 0;
    const n = Math.min(columns(grid), cards.length);
    let h = 0;
    for (let i = 0; i < n; i++) h = Math.max(h, cards[i].offsetHeight);
    return h + 2; // keep borders/shadows of the first row visible
  }
  function setFolded(section, folded) {
    const grid = section.querySelector('[data-tb-grid]');
    const n = columns(grid);
    [...grid.children].forEach((card, i) => {
      const hide = folded && i >= n;
      card.classList.toggle('is-folded', hide);
      if (hide) { card.setAttribute('inert', ''); card.setAttribute('aria-hidden', 'true'); }
      else { card.removeAttribute('inert'); card.removeAttribute('aria-hidden'); }
    });
  }

  async function loadRemaining(section, btn) {
    // Fetch every remaining page of the collection with the Section Rendering API.
    let next = btn.dataset.nextUrl;
    if (!next) return;
    const grid = section.querySelector('[data-tb-grid]');
    btn.setAttribute('aria-busy', 'true');
    try {
      while (next) {
        const sep = next.includes('?') ? '&' : '?';
        const res = await fetch(`${next}${sep}section_id=${section.dataset.sectionId}`, { credentials: 'same-origin' });
        if (!res.ok) break;
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const known = new Set([...grid.querySelectorAll('[data-product-url]')].map((c) => c.dataset.productUrl));
        doc.querySelectorAll('[data-tb-grid] > [data-tb-card]').forEach((card) => {
          if (known.has(card.dataset.productUrl)) return; // never duplicate
          card.classList.add('is-folded');
          grid.appendChild(document.importNode(card, true));
        });
        const b = doc.querySelector('[data-tb-toggle]');
        next = b && b.dataset.nextUrl;
      }
      grid.querySelectorAll('[data-tb-card]').forEach(bindCard);
    } finally {
      delete btn.dataset.nextUrl;
      btn.removeAttribute('aria-busy');
    }
  }

  function initSection(section) {
    if (section.dataset.tbInit) return;
    section.dataset.tbInit = '1';
    const viewport = section.querySelector('[data-tb-viewport]');
    const grid = section.querySelector('[data-tb-grid]');
    const btn = section.querySelector('[data-tb-toggle]');
    const label = btn && btn.querySelector('.tb-col__label');
    grid.querySelectorAll('[data-tb-card]').forEach(bindCard);
    if (!btn) { viewport.classList.add('is-static'); return; }

    let expanded = false;
    const collapse = (animate) => {
      const target = firstRowHeight(grid);
      if (!animate || reduceMotion()) { viewport.style.height = `${target}px`; setFolded(section, true); return; }
      viewport.style.height = `${viewport.scrollHeight}px`;
      viewport.offsetHeight; // reflow so the transition starts from the current height
      viewport.style.height = `${target}px`;
      const done = () => setFolded(section, true);
      viewport.addEventListener('transitionend', done, { once: true });
      setTimeout(done, 650);
    };
    const expand = () => {
      setFolded(section, false);
      const from = viewport.offsetHeight;
      viewport.style.height = `${from}px`;
      const to = grid.scrollHeight;
      if (reduceMotion()) { viewport.style.height = 'auto'; return; }
      requestAnimationFrame(() => {
        viewport.style.height = `${to}px`;
        const done = () => { if (expanded) viewport.style.height = 'auto'; };
        viewport.addEventListener('transitionend', done, { once: true });
        setTimeout(done, 650);
      });
    };

    collapse(false);
    // hide the button when everything already fits in one row
    const updateVisibility = () => {
      const allFit = grid.children.length <= columns(grid) && !btn.dataset.nextUrl;
      btn.parentElement.hidden = allFit;
      if (!expanded) collapse(false);
    };
    updateVisibility();
    window.addEventListener('resize', () => { if (!expanded) updateVisibility(); }, { passive: true });
    // images may change the row height after load
    grid.querySelectorAll('img').forEach((im) => im.complete || im.addEventListener('load', () => { if (!expanded) collapse(false); }, { once: true }));

    btn.addEventListener('click', async () => {
      if (!expanded) {
        await loadRemaining(section, btn);
        expanded = true;
        btn.setAttribute('aria-expanded', 'true');
        label.textContent = btn.dataset.less; section.classList.add('tb-col--open');
        expand();
      } else {
        expanded = false;
        btn.setAttribute('aria-expanded', 'false');
        label.textContent = btn.dataset.more; section.classList.remove('tb-col--open');
        // keep the visitor oriented: if the heading is above the viewport, bring the section back first
        const top = section.getBoundingClientRect().top;
        if (top < 0) section.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
        collapse(true);
        btn.focus({ preventScroll: true });
      }
    });
  }

  const boot = (scope = document) => {
    scope.querySelectorAll('[data-tb-collection]').forEach(initSection);
    scope.querySelectorAll('[data-tb-card]').forEach(bindCard); // collection, search and any other page
      };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();
  // cards re-rendered by filters / sorting / pagination
  new MutationObserver(() => document.querySelectorAll('[data-tb-card]:not([data-tb-bound])').forEach(bindCard)).observe(document.documentElement, { childList: true, subtree: true });
  // theme editor support
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
